import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { generateTaxFormPDF } from "./pdfGenerator";
import { getQuestions } from "./questionDataService";
import companyLogo from "../../Assets/BooksCompl.png";
import PDFPreviewModal from "./PDFPreviewModal";
import {
  FaTimes, FaChevronDown, FaChevronUp, FaFile, FaTrash,
  FaSave, FaCheck, FaSpinner, FaClock, FaUpload
} from "react-icons/fa";

const MAX_FILES_PER_QUESTION = 5;
const MAX_SIZE_PER_QUESTION = 50 * 1024 * 1024; // 50 MB
const MAX_BOTTOM_FILES = 10;
const MAX_BOTTOM_SIZE = 100 * 1024 * 1024; // 100 MB

const QuestionsForm = ({
  onClose, onSubmit, initialAnswers, initialComments, initialFiles = [], isSubmitted, formNumber, memberType = "primary", memberIndex = 0, memberName = ""
}) => {
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [comments, setComments] = useState(initialComments || {});
  const [expanded, setExpanded] = useState({});
  // Per-question files: { [questionKey]: [{ file, name, size, type, url, isStored }] }
  const [questionFiles, setQuestionFiles] = useState({});
  const [bottomFiles, setBottomFiles] = useState([]);
  const [formQuestions, setFormQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [previewPDFBlob, setPreviewPDFBlob] = useState(null);
  const [uploadedQuestionFileUrls, setUploadedQuestionFileUrls] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const questions = await getQuestions(formNumber);
        setFormQuestions(questions);
        setError('');
      } catch (err) {
        setError('Failed to load questions. Please try again.');
        console.error('Error fetching questions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [formNumber]);

  // Restore per-question files from initialFiles (stored as { questionKey, name, size, type, url })
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      const restoredQuestions = {};
      const restoredBottom = [];
      
      initialFiles.forEach(file => {
        const key = file.questionKey;
        const fileData = {
          name: file.name,
          size: file.size,
          type: file.type,
          url: file.url,
          isStored: true
        };

        if (key === '_bottom') {
          restoredBottom.push(fileData);
        } else if (key) {
          if (!restoredQuestions[key]) restoredQuestions[key] = [];
          restoredQuestions[key].push(fileData);
        }
      });
      
      setQuestionFiles(restoredQuestions);
      setBottomFiles(restoredBottom);
    } else {
      setQuestionFiles({});
      setBottomFiles([]);
    }
  }, [initialFiles]);

  useEffect(() => {
    if (!modalRoot) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; };
  }, [modalRoot]);

  const totalQuestions = Object.values(formQuestions).reduce(
    (total, section) => total + section.length, 0
  );

  const handleAnswer = (index, value) => {
    if (!isSubmitted) {
      setAnswers((prev) => ({ ...prev, [index]: value }));
      setIsDirty(true);
      setSubmitError('');
    }
  };

  const handleComment = (index, value) => {
    if (!isSubmitted) {
      setComments((prev) => ({ ...prev, [index]: value }));
      setIsDirty(true);
    }
  };

  const toggleQuestion = (question) => {
    if (!isSubmitted) {
      setExpanded((prev) => ({ ...prev, [question]: !prev[question] }));
    }
  };

  const calculateProgress = () => {
    return Object.keys(answers).filter((k) => answers[k] !== undefined).length;
  };

  const calculateSectionProgress = (section) => {
    return formQuestions[section].filter((_, index) => answers[`${section}-${index}`] !== undefined).length;
  };

  // ---- Per-question file handlers (Part A only) ----
  const handleQuestionFileChange = (e, questionKey) => {
    if (isSubmitted) return;
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;

    setQuestionFiles(prev => {
      const existing = prev[questionKey] || [];
      const available = MAX_FILES_PER_QUESTION - existing.length;
      if (available <= 0) {
        alert(`You can only upload up to ${MAX_FILES_PER_QUESTION} files per question.`);
        return prev;
      }
      const toAdd = newFiles.slice(0, available);
      if (newFiles.length > available) {
        alert(`Only ${available} file(s) can be added. Max ${MAX_FILES_PER_QUESTION} per question.`);
      }
      // Check total size for this question
      const existingSize = existing.reduce((sum, f) => sum + (f.size || 0), 0);
      const newSize = toAdd.reduce((sum, f) => sum + (f.size || 0), 0);
      if (existingSize + newSize > MAX_SIZE_PER_QUESTION) {
        alert(`Total file size per question cannot exceed 50 MB.`);
        return prev;
      }
      const formatted = toAdd.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0
      }));
      return { ...prev, [questionKey]: [...existing, ...formatted] };
    });
    setIsDirty(true);
    setSubmitError('');
    e.target.value = '';
  };

  // ---- Bottom general file upload handlers ----
  const handleBottomFileChange = (e) => {
    if (isSubmitted) return;
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;

    setBottomFiles(prev => {
      const available = MAX_BOTTOM_FILES - prev.length;
      if (available <= 0) {
        alert(`You can only upload up to ${MAX_BOTTOM_FILES} files.`);
        return prev;
      }
      const toAdd = newFiles.slice(0, available);
      if (newFiles.length > available) {
        alert(`Only ${available} file(s) can be added. Max ${MAX_BOTTOM_FILES} files.`);
      }
      const existingSize = prev.reduce((sum, f) => sum + (f.size || 0), 0);
      const newSize = toAdd.reduce((sum, f) => sum + (f.size || 0), 0);
      if (existingSize + newSize > MAX_BOTTOM_SIZE) {
        alert(`Total file size cannot exceed 100 MB.`);
        return prev;
      }
      const formatted = toAdd.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0
      }));
      return [...prev, ...formatted];
    });
    setIsDirty(true);
    e.target.value = '';
  };

  const removeBottomFile = (fileIndex) => {
    if (isSubmitted) return;
    setBottomFiles(prev => prev.filter((_, i) => i !== fileIndex));
    setIsDirty(true);
  };

  const getBottomFilesTotalSize = () => {
    return bottomFiles.reduce((sum, f) => sum + (f.size || 0), 0);
  };

  const getQuestionFilesTotalSize = (questionKey) => {
    return (questionFiles[questionKey] || []).reduce((sum, f) => sum + (f.size || 0), 0);
  };

  const removeQuestionFile = (questionKey, fileIndex) => {
    if (isSubmitted) return;
    setQuestionFiles(prev => {
      const updated = (prev[questionKey] || []).filter((_, i) => i !== fileIndex);
      return { ...prev, [questionKey]: updated };
    });
    setIsDirty(true);
  };

  const getPartASectionName = () => {
    const keys = Object.keys(formQuestions);
    return keys.length > 0 ? keys[0] : null;
  };

  const isPartAQuestion = (questionTitle) => {
    return questionTitle === getPartASectionName();
  };

  // Check if all Part A "yes" questions have at least one file
  const validatePartAFiles = () => {
    const partASection = getPartASectionName();
    if (!partASection || !formQuestions[partASection]) return true;

    for (let i = 0; i < formQuestions[partASection].length; i++) {
      const key = `${partASection}-${i}`;
      const ans = answers[key];
      const files = questionFiles[key] || [];
      const comment = comments[key] || "";
      
      if (ans === 'yes') {
        if (files.length === 0) return false;
      } else if (ans === 'comment') {
        if (files.length === 0 || comment.trim() === '') return false;
      }
    }
    return true;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Build a renamed filename: originalName_A-{partNum}_Q{qNum}.ext
  const buildRenamedFileName = (originalName, partLetter, questionNumber) => {
    const lastDot = originalName.lastIndexOf('.');
    if (lastDot === -1) return `${originalName}_${partLetter}_Q${questionNumber}`;
    const base = originalName.substring(0, lastDot);
    const ext = originalName.substring(lastDot); // includes the dot
    return `${base}_${partLetter}_Q${questionNumber}${ext}`;
  };

  const handleSave = async () => {
    if (!isSubmitted) {
      try {
        const token = localStorage.getItem("token");
        const currentQuestionFiles = { ...questionFiles };
        const sectionKeys = Object.keys(formQuestions);

        // Upload any new unsaved files first
        for (const [questionKey, files] of Object.entries(currentQuestionFiles)) {
          const newFiles = files.filter(f => f.file && !f.isStored);
          if (newFiles.length === 0) continue;

          const [sectionName, subIndexStr] = questionKey.split(/-(?=\d+$)/);
          const subIndex = parseInt(subIndexStr, 10);
          const sectionIdx = sectionKeys.indexOf(sectionName);
          const partLetter = `${String.fromCharCode(65 + sectionIdx)}-${sectionIdx + 1}`;

          const formData = new FormData();
          newFiles.forEach(f => {
            const renamed = buildRenamedFileName(f.name, partLetter, subIndex + 1);
            const renamedFile = new File([f.file], renamed, { type: f.type });
            formData.append('files', renamedFile);
          });

          // USE TEMP=TRUE so it doesn't show in Documents yet
          const uploadResp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-files?temp=true`, {
            method: "POST",
            body: formData,
            headers: { Authorization: `Bearer ${token}` },
          });

          if (uploadResp.ok) {
            const uploadResult = await uploadResp.json();
            const fileUrls = uploadResult.fileUrls || [];
            
            // Update the local state for this question
            const updatedFilesForQuestion = [...files];
            let urlIdx = 0;
            updatedFilesForQuestion.forEach((f, fi) => {
              if (f.file && !f.isStored) {
                const renamed = buildRenamedFileName(f.name, partLetter, subIndex + 1);
                const match = fileUrls.find(u => typeof u === 'object' ? (u.originalName === renamed || u.originalName === f.name) : (u.includes(encodeURIComponent(renamed)) || u.includes(encodeURIComponent(f.name))));
                
                updatedFilesForQuestion[fi] = {
                  ...f,
                  url: typeof match === 'object' ? match.fileUrl : (match || ''),
                  isStored: true,
                  file: null // Clear the File object
                };
              }
            });
            currentQuestionFiles[questionKey] = updatedFilesForQuestion;
          }
        }

        setQuestionFiles(currentQuestionFiles);

        // Upload bottom files
        let currentBottomFiles = [...bottomFiles];
        const newBottomFiles = currentBottomFiles.filter(f => f.file && !f.isStored);
        if (newBottomFiles.length > 0) {
          const bottomFormData = new FormData();
          newBottomFiles.forEach(f => {
            bottomFormData.append('files', new File([f.file], f.name, { type: f.type }));
          });
          const bottomUploadResp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-files?temp=true`, {
            method: "POST",
            body: bottomFormData,
            headers: { Authorization: `Bearer ${token}` },
          });
          if (bottomUploadResp.ok) {
            const bottomResult = await bottomUploadResp.json();
            const bottomUrls = bottomResult.fileUrls || [];
            currentBottomFiles = currentBottomFiles.map(f => {
              if (f.file && !f.isStored) {
                const match = bottomUrls.find(u => typeof u === 'object' ? (u.originalName === f.name) : (u.includes(encodeURIComponent(f.name))));
                return { ...f, url: typeof match === 'object' ? match.fileUrl : (match || ''), isStored: true, file: null };
              }
              return f;
            });
            setBottomFiles(currentBottomFiles);
          }
        }

        // Flatten per-question files for storage
        const flatFiles = [];
        const sectionKeysLocal = Object.keys(formQuestions);
        Object.keys(currentQuestionFiles).forEach(key => {
          const [sectionName, subIndexStr] = key.split(/-(?=\d+$)/);
          const subIndex = parseInt(subIndexStr, 10);
          const sectionIdx = sectionKeysLocal.indexOf(sectionName);
          const partLetter = `${String.fromCharCode(65 + sectionIdx)}-${sectionIdx + 1}`;

          (currentQuestionFiles[key] || []).forEach(file => {
            flatFiles.push({
              questionKey: key,
              name: file.name,
              size: file.size,
              type: file.type,
              url: file.url || '',
              renamedName: buildRenamedFileName(file.name, partLetter, subIndex + 1)
            });
          });
        });

        // Add bottom files
        currentBottomFiles.forEach(file => {
          flatFiles.push({
            questionKey: '_bottom',
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.url || '',
            renamedName: file.name
          });
        });

        await onSubmit({ answers, comments, files: flatFiles }, false, memberType, memberIndex);
        setIsDirty(false);
        const currentProgress = calculateProgress();
        alert(`Saved progress: ${currentProgress}/${totalQuestions} questions answered`);
      } catch (err) {
        console.error("Error saving progress:", err);
        alert("Failed to save progress: " + err.message);
      }
    }
  };

  const handleClose = () => {
    if (isDirty && !isSubmitted) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  };

  const handleConfirm = async () => {
    const currentProgress = calculateProgress();
    if (totalQuestions === 0) {
      alert("Questions are still loading. Please wait a moment before submitting.");
      return;
    }
    if (currentProgress < totalQuestions) {
      alert(`Please answer all ${totalQuestions} checklist questions before submitting.`);
      return;
    }

    // Validate Part A file uploads
    if (!validatePartAFiles()) {
      setSubmitError('👉 Please upload required documents before submitting.');
      return;
    }
    setSubmitError('');

    if (!isSubmitted) {
      try {
        // Upload all per-question new files
        const token = localStorage.getItem("token");
        const allUploadedUrls = {}; // { questionKey: [url, ...] }

        const partASection = getPartASectionName();
        const sectionKeys = Object.keys(formQuestions);

        for (const [questionKey, files] of Object.entries(questionFiles)) {
          const newFiles = files.filter(f => f.file && !f.isStored);
          if (newFiles.length === 0) continue;

          // Determine part letter and question number for naming
          const [sectionName, subIndexStr] = questionKey.split(/-(?=\d+$)/);
          const subIndex = parseInt(subIndexStr, 10);
          const sectionIdx = sectionKeys.indexOf(sectionName);
          const partLetter = `${String.fromCharCode(65 + sectionIdx)}-${sectionIdx + 1}`;

          const formData = new FormData();
          newFiles.forEach((f, fi) => {
            const renamed = buildRenamedFileName(f.name, partLetter, subIndex + 1);
            const renamedFile = new File([f.file], renamed, { type: f.type });
            formData.append('files', renamedFile);
          });

          // USE TEMP=TRUE so it doesn't show in Documents yet
          const uploadResp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-files?temp=true`, {
            method: "POST",
            body: formData,
            headers: { Authorization: `Bearer ${token}` },
          });

          if (!uploadResp.ok) throw new Error("Failed to upload files for question " + questionKey);
          const uploadResult = await uploadResp.json();
          allUploadedUrls[questionKey] = uploadResult.fileUrls || [];
        }

        // Upload any new bottom files as well
        const newBottomFiles = bottomFiles.filter(f => f.file && !f.isStored);
        if (newBottomFiles.length > 0) {
          const bottomFormData = new FormData();
          newBottomFiles.forEach(f => {
            bottomFormData.append('files', new File([f.file], f.name, { type: f.type }));
          });
          const bottomUploadResp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-files?temp=true`, {
            method: "POST",
            body: bottomFormData,
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!bottomUploadResp.ok) throw new Error("Failed to upload additional files");
          const bottomResult = await bottomUploadResp.json();
          allUploadedUrls['_bottom'] = bottomResult.fileUrls || [];
        }

        setUploadedQuestionFileUrls(allUploadedUrls);

        // Generate PDF preview
        const pdfBlob = generateTaxFormPDF(
          formQuestions, answers, comments, formNumber, companyLogo,
          memberType, memberIndex, null
        );
        setPreviewPDFBlob(pdfBlob);
        setShowPDFPreview(true);
      } catch (error) {
        console.error("Error preparing submission:", error);
        alert(`Failed to prepare submission: ${error.message}`);
      }
    }
  };

  const handlePDFPreviewConfirm = async (signatureData) => {
    setIsSubmitting(true);
    try {
      const signedPDFBlob = generateTaxFormPDF(
        formQuestions, answers, comments, formNumber, companyLogo,
        memberType, memberIndex, signatureData
      );

      const pdfFormData = new FormData();
      const currentYear = new Date().getFullYear();
      let pdfName = `Info.Checklist filled ${currentYear}.pdf`;
      if (memberType !== "primary") {
        pdfName = `Info.Checklist filled ${currentYear} (${memberName || `Member ${memberIndex + 1}`}).pdf`;
      }
      pdfFormData.append('pdf', signedPDFBlob, pdfName);

      const pdfToken = localStorage.getItem("token");
      const pdfResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-pdf`, {
        method: "POST",
        body: pdfFormData,
        headers: { Authorization: `Bearer ${pdfToken}` },
      });

      if (!pdfResponse.ok) throw new Error("Failed to upload signed PDF");
      const pdfResult = await pdfResponse.json();

      // Build flat list of all per-question submitted files for backend storage
      const sectionKeys = Object.keys(formQuestions);
      const submittedFiles = [];

      Object.keys(questionFiles).forEach(questionKey => {
        const files = questionFiles[questionKey] || [];
        const [sectionName, subIndexStr] = questionKey.split(/-(?=\d+$)/);
        const subIndex = parseInt(subIndexStr, 10);
        const sectionIdx = sectionKeys.indexOf(sectionName);
        const partLetter = `${String.fromCharCode(65 + sectionIdx)}-${sectionIdx + 1}`;

        files.forEach(file => {
          if (file.isStored && file.url) {
            submittedFiles.push({
              questionKey,
              name: file.name,
              size: file.size,
              type: file.type,
              url: file.url,
              renamedName: buildRenamedFileName(file.name, partLetter, subIndex + 1)
            });
          } else {
            // Find URL from uploaded
            const urls = uploadedQuestionFileUrls[questionKey] || [];
            const renamed = buildRenamedFileName(file.name, partLetter, subIndex + 1);
            const matchUrl = urls.find(u => typeof u === 'string' && (u.includes(encodeURIComponent(renamed)) || u.includes(encodeURIComponent(file.name))));
            submittedFiles.push({
              questionKey,
              name: file.name,
              size: file.size,
              type: file.type,
              url: matchUrl || '',
              renamedName: renamed
            });
          }
        });
      });

      // Also include bottom files in final submission
      bottomFiles.forEach(file => {
        if (file.isStored && file.url) {
          submittedFiles.push({
            questionKey: '_bottom',
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.url,
            renamedName: file.name
          });
        } else {
          // Find URL from uploaded
          const urls = uploadedQuestionFileUrls['_bottom'] || [];
          const matchUrl = urls.find(u => {
            const fileName = typeof u === 'object' ? u.originalName : u;
            return fileName.includes(encodeURIComponent(file.name)) || fileName.includes(file.name);
          });
          submittedFiles.push({
            questionKey: '_bottom',
            name: file.name,
            size: file.size,
            type: file.type,
            url: typeof matchUrl === 'object' ? matchUrl.fileUrl : (matchUrl || ''),
            renamedName: file.name
          });
        }
      });

      await onSubmit({
        answers,
        comments,
        pdfUrl: pdfResult.pdfUrl,
        files: submittedFiles,
        signatureData
      }, true, memberType, memberIndex);

      setShowPDFPreview(false);
      setTimeout(() => { alert("Form submitted successfully!"); }, 300);
    } catch (error) {
      console.error("Error in final submission:", error);
      alert(`Failed to submit form: ${error.message}`);
      setShowPDFPreview(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!modalRoot) return null;

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-2xl">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-primary-200 dark:border-primary-800 border-t-primary-500 rounded-full animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading questions...</p>
          </div>
        </div>
      </div>,
      modalRoot
    );
  }

  if (error) {
    return createPortal(
      <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-2xl max-w-md">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <FaTimes className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 hover:shadow-xl transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>,
      modalRoot
    );
  }

  const getOptionStyles = (option, isSelected) => {
    if (!isSelected) return 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600';
    switch (option) {
      case 'yes': return 'bg-green-500 text-white shadow-lg shadow-green-500/30';
      case 'no': return 'bg-red-500 text-white shadow-lg shadow-red-500/30';
      case 'na': return 'bg-gray-500 text-white shadow-lg shadow-gray-500/30';
      case 'comment': return 'bg-primary-500 text-white shadow-lg shadow-primary-500/30';
      default: return 'bg-gray-100 dark:bg-dark-700';
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4 overflow-y-auto">
      <div className="bg-white dark:bg-dark-800 md:rounded-2xl rounded-none shadow-2xl w-full md:max-w-7xl h-full md:max-h-[90vh] flex flex-col animate-scale-in">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-gray-100 dark:border-dark-700">
          <div className="flex items-center justify-between mb-2 md:mb-4">
            <h2 className="text-base md:text-2xl font-bold text-dark-800 dark:text-white">
              Initial Information (Checklist)
            </h2>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold text-sm">
                {calculateProgress()}/{totalQuestions}
              </span>
              <button
                onClick={handleClose}
                className="p-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="p-2 md:p-3 rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800/30">
            <div className="space-y-1 text-xs md:text-sm text-primary-700 dark:text-primary-300">
              <p><span className="font-semibold">1.</span> Please upload documents in Part A.</p>
              <p><span className="font-semibold">2.</span> Parts B, C etc. will be completed by our team for your review and confirmation.</p>
            </div>
          </div>
        </div>

        {/* Questions Container */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {Object.keys(formQuestions).map((questionTitle, index) => {
            const sectionLetter = String.fromCharCode(65 + index);
            const sectionProgress = calculateSectionProgress(questionTitle);
            const isExpanded = expanded[questionTitle];
            const isPartA = index === 0;
            const sectionIsPending = isPartA && formQuestions[questionTitle].some((_, subIdx) => {
              const key = `${questionTitle}-${subIdx}`;
              const ans = answers[key];
              const qFiles = questionFiles[key] || [];
              const showUpload = ans === 'yes' || ans === 'comment';
              const commentFilled = ans === 'comment' ? (comments[key] && comments[key].trim() !== '') : true;
              return showUpload && (qFiles.length === 0 || !commentFilled);
            });
            return (
              <div key={index} className="rounded-2xl border border-gray-200 dark:border-dark-600 overflow-hidden shadow-sm">
                {/* Section Header */}
                <button
                  onClick={() => toggleQuestion(questionTitle)}
                  className="w-full flex items-center justify-between p-4 bg-white dark:bg-dark-700 hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white font-bold text-sm flex items-center justify-center shadow-md shadow-primary-500/30">
                      {sectionLetter}
                    </span>
                    <span className="font-semibold text-dark-800 dark:text-white text-left text-base">{questionTitle}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {sectionIsPending && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] md:text-xs font-bold border border-amber-200 dark:border-amber-800/30 whitespace-nowrap">
                        <FaClock className="w-2.5 h-2.5" />
                        Pending
                      </span>
                    )}
                    <span className="px-3 py-1.5 rounded-full bg-gray-100 dark:bg-dark-600 text-sm font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-dark-500">
                      {sectionProgress}/{formQuestions[questionTitle].length}
                    </span>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isExpanded ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-gray-100 dark:bg-dark-600'}`}>
                      {isExpanded ? <FaChevronUp className="w-4 h-4 text-primary-500" /> : <FaChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </div>
                </button>

                {/* Section Content */}
                {isExpanded && (
                  <div className="p-4 space-y-3 bg-gray-50 dark:bg-dark-800 border-t border-gray-200 dark:border-dark-600">
                    {formQuestions[questionTitle].map((subQuestion, subIndex) => {
                      const questionKey = `${questionTitle}-${subIndex}`;
                      const currentAnswer = answers[questionKey];
                      const qFiles = questionFiles[questionKey] || [];
                      const hasFile = qFiles.length > 0;
                      const showUpload = isPartA && (currentAnswer === 'yes' || currentAnswer === 'comment');
                      const commentFilled = currentAnswer === 'comment' ? (comments[questionKey] && comments[questionKey].trim() !== '') : true;
                      const isPending = showUpload && (!hasFile || !commentFilled);
                      const isCompleted = showUpload && hasFile && commentFilled;

                      return (
                        <div key={subIndex} className="p-4 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 shadow-sm">
                          {/* Question row with status badge */}
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <p className="text-dark-800 dark:text-gray-200 leading-relaxed flex-1">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-semibold text-xs mr-2">{subIndex + 1}</span>
                              {subQuestion}
                            </p>
                            {/* Status Badge */}
                            {isPending && (
                              <span className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] md:text-xs font-bold border border-amber-200 dark:border-amber-800/30 whitespace-nowrap">
                                <FaClock className="w-2.5 h-2.5" />
                                Pending
                              </span>
                            )}
                            {isCompleted && (
                              <span className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] md:text-xs font-bold border border-green-200 dark:border-green-800/30 whitespace-nowrap">
                                <FaCheck className="w-2.5 h-2.5" />
                                Completed
                              </span>
                            )}
                          </div>

                          {/* Answer buttons */}
                          <div className="flex gap-1.5">
                            {["yes", "no", "na", "comment"].map((option) => (
                              <button
                                key={option}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAnswer(questionKey, option);
                                }}
                                disabled={isSubmitted}
                                className={`flex-1 md:flex-none md:w-24 px-1 py-1.5 md:py-1 rounded-lg font-bold text-xs transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${getOptionStyles(option, currentAnswer === option)}`}
                              >
                                {option.toUpperCase()}
                              </button>
                            ))}
                          </div>

                          {/* Comment box */}
                          {currentAnswer === "comment" && (
                            <textarea
                              placeholder="Add a comment..."
                              value={comments[questionKey] || ""}
                              onChange={(e) => handleComment(questionKey, e.target.value)}
                              disabled={isSubmitted}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-4 w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all duration-200 resize-none"
                              rows={2}
                            />
                          )}

                          {/* Part A: Upload section when "yes" selected */}
                          {showUpload && !isSubmitted && (
                            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-dark-600/50">
                              {/* Upload Button */}
                              {qFiles.length < MAX_FILES_PER_QUESTION && (
                                <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-primary-400 dark:border-primary-600 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 cursor-pointer text-xs font-semibold text-primary-600 dark:text-primary-400 transition-all duration-200">
                                  <input
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => handleQuestionFileChange(e, questionKey)}
                                    disabled={isSubmitted}
                                  />
                                  <FaUpload className="w-3 h-3" />
                                  + Upload File ({qFiles.length}/{MAX_FILES_PER_QUESTION})
                                </label>
                              )}
                              <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                <span className="text-[10px] md:text-xs text-gray-400 dark:text-gray-500 font-medium whitespace-nowrap">
                                  Max {MAX_FILES_PER_QUESTION} files • 50 MB
                                </span>
                                <span className="hidden sm:inline text-gray-300 dark:text-gray-600">|</span>
                                <span className="text-[10px] md:text-xs text-primary-500/70 dark:text-primary-400/70 font-medium">
                                  {formatFileSize(getQuestionFilesTotalSize(questionKey))} used
                                </span>
                              </div>

                              {/* Uploaded files list */}
                              {qFiles.length > 0 && (
                                <div className="mt-2 space-y-1.5">
                                  {qFiles.map((file, fi) => (
                                    <div key={fi} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-600">
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <FaFile className="w-3 h-3 text-primary-500 flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                          <p className="text-xs text-dark-800 dark:text-white truncate font-medium" title={file.name}>{file.name}</p>
                                          <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => removeQuestionFile(questionKey, fi)}
                                        className="p-1 text-red-500 hover:text-red-600 transition-colors flex-shrink-0 ml-2"
                                        title="Remove file"
                                      >
                                        <FaTrash className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Read-only view of uploaded files when submitted */}
                          {isSubmitted && isPartA && qFiles.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-dark-600/50 space-y-1.5">
                              {qFiles.map((file, fi) => (
                                <div key={fi} className="flex items-center gap-2 py-1.5 px-2.5 rounded-lg bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-600">
                                  <FaFile className="w-3 h-3 text-green-500 flex-shrink-0" />
                                  <p className="text-xs text-dark-800 dark:text-white truncate flex-1">{file.name}</p>
                                  <span className="text-xs text-gray-400">{formatFileSize(file.size)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Bottom Upload Files Section */}
          {!isSubmitted && (
            <div className="mt-4 rounded-2xl border border-gray-200 dark:border-dark-600 overflow-hidden shadow-sm">
              <div className="p-4 bg-white dark:bg-dark-700">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-white font-bold text-sm flex items-center justify-center shadow-md shadow-indigo-500/30">
                    <FaUpload className="w-4 h-4" />
                  </span>
                  <div>
                    <span className="font-semibold text-dark-800 dark:text-white text-base">Upload Files</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Upload any additional documents here</p>
                  </div>
                </div>

                {/* Upload Button */}
                {bottomFiles.length < MAX_BOTTOM_FILES && (
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-indigo-400 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 cursor-pointer text-sm font-semibold text-indigo-600 dark:text-indigo-400 transition-all duration-200">
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleBottomFileChange}
                    />
                    <FaUpload className="w-3.5 h-3.5" />
                    + Upload Files ({bottomFiles.length}/{MAX_BOTTOM_FILES})
                  </label>
                )}

                {/* Uploaded files list */}
                {bottomFiles.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {bottomFiles.map((file, fi) => (
                      <div key={fi} className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-600">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FaFile className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-dark-800 dark:text-white truncate font-medium" title={file.name}>{file.name}</p>
                            <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeBottomFile(fi)}
                          className="p-1 text-red-500 hover:text-red-600 transition-colors flex-shrink-0 ml-2"
                          title="Remove file"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Size info */}
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <span>Max {MAX_BOTTOM_FILES} files</span>
                  <span>•</span>
                  <span>Max 100 MB total ({formatFileSize(getBottomFilesTotalSize())} used)</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submit Error Banner */}
        {submitError && (
          <div className="mx-4 mb-2 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 text-red-700 dark:text-red-400 text-sm font-medium">
            {submitError}
          </div>
        )}

        {/* Actions */}
        <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 bg-white dark:bg-dark-800">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {!isSubmitted && (
              <button
                onClick={handleSave}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-all duration-200"
              >
                <FaSave className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Save Inprogress</span>
                <span className="sm:hidden">Save</span>
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={isSubmitted || isSubmitting}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isSubmitting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaCheck className="w-4 h-4" />}
              <span className="text-sm">Submit</span>
            </button>
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPDFPreview && previewPDFBlob && (
        <PDFPreviewModal
          pdfBlob={previewPDFBlob}
          onClose={() => setShowPDFPreview(false)}
          onConfirm={handlePDFPreviewConfirm}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Unsaved Changes Confirmation Modal */}
      {showUnsavedModal && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Unsaved Changes</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">You have unsaved changes. Are you sure you want to close without saving?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUnsavedModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-400 dark:hover:bg-dark-600 transition-colors"
              >
                Stay
              </button>
              <button
                onClick={() => { setShowUnsavedModal(false); onClose(); }}
                className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors"
              >
                Close Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    modalRoot
  );
};

export default QuestionsForm;